import { supertypeClass } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import _ from 'lodash';
import { CollectionItemChange, PolicyChangeHistory, ValueType } from '../customer/PolicyChangeHistory';
import { Policy } from '../customer/Policy';
import { POLICY_CHANGE_HISTORY_STATUS } from '../static/PolicyChangeHistoryStatus';

@supertypeClass({ toClient: true, toServer: true })
export class PolicyChangeHistoryManager extends Everything {
  constructor() {
    super();
  }

  static new() {
    return new PolicyChangeHistoryManager().init();
  }

  init() {
    return this;
  }

  static getPendingChangesForPolicy(policy: Policy) {
    return policy.policyChanges.filter((amendment) => amendment.status === POLICY_CHANGE_HISTORY_STATUS.PENDING);
  }

  static finalizePendingChangesForPolicy(policy: Policy) {
    function updatePolicyProperty(policyChange: PolicyChangeHistory) {
      // Apply the policy change onto the policy.
      // The property can be right on the policy or as part of an entity linked to the policy
      const property: string = policyChange.property;

      if (!property) {
        throw new Error('Unable to amend policy without a valid property');
      }

      const propertyTrail: string[] = property.split('.');
      const trailLength: number = propertyTrail.length;
      let currentObjectToModify = policy;

      for (let i = 0; i < trailLength; i++) {
        const currentAttribute = propertyTrail[i];

        if (i === trailLength - 1) {
          currentObjectToModify[currentAttribute] = convertValue(policyChange, currentObjectToModify[currentAttribute]);
          return;
        } else {
          currentObjectToModify = currentObjectToModify[currentAttribute];

          if (!currentObjectToModify) {
            throw new Error('Unable to find policy attribute for modification: ' + currentAttribute);
          }
        }
      }
    }

    function updatePolicyProperties(changeAmendments: PolicyChangeHistory[]) {
      if (changeAmendments) {
        changeAmendments.forEach((changeAmendment) => {
          updatePolicyProperty(changeAmendment);
          changeAmendment.status = POLICY_CHANGE_HISTORY_STATUS.COMPLETE;
        });
      }
    }

    function convertValue(policyChange: PolicyChangeHistory, currValue) {
      const newValue: string = policyChange.newValue;
      const valueType: ValueType = policyChange.valueType;
      const errMsg: string = `Unable to apply policy change for: ${policyChange.property}`;

      switch (valueType) {
        case ValueType.number:
          return parseFloat(newValue);

        case ValueType.date:
          return new Date(newValue);

        case ValueType.boolean:
          return newValue === 'true';

        case ValueType.json:
          // Expecting an array of CollectionItemChange objects
          const changes: CollectionItemChange[] = JSON.parse(newValue);
          if (Array.isArray(currValue)) {
            return changes.map((change) => {
              // for each change, find the corresponding item in the current data model collection
              // and apply the change
              const modelValue = currValue.find((val) => val[change.uniquePropertyName] === change.uniquePropertyValue);

              if (modelValue) {
                modelValue[change.propBeingChanged] = change.newValue;
              } else {
                throw errMsg;
              }
              return modelValue;
            });
          } else {
            throw errMsg;
          }

        case ValueType.string:
        default:
          return newValue;
      }
    }

    const pendingChanges = this.getPendingChangesForPolicy(policy);

    return updatePolicyProperties(pendingChanges);
  }

  static getPendingChangeForProperty(policy, property) {
    let pendingChange;

    if (policy.policyChanges) {
      pendingChange = _.chain(policy.policyChanges)
        .filter({ status: POLICY_CHANGE_HISTORY_STATUS.PENDING, property: property })
        .sortBy('createdTime')
        .first()
        .value();
    }

    return pendingChange;
  }
}
